import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import styles from '../styles/user-dashboard.module.css';
import { isAuthenticated, logout } from '../utils/auth';
import EditProfileModal from './EditProfileModal';
import apiClient from '../utils/apiClient';
import { handleAuthError } from '../utils/authErrorHandler';
import { secureStorage } from '../utils/secureStorage'; // 🔒 SÉCURITÉ - Stockage chiffré
import EntityJoinRequest from './EntityJoinRequest';
import UserEntityRequests from './UserEntityRequests';
import EntityJoinModal from './EntityJoinModal';
import LicenseManagement from './LicenseManagement';
import LicenseStatusWidget from './LicenseStatusWidget';
import UsageDashboard from './UsageDashboard';

// TODO: Improve tenant management in relation to graphs

const UserDashboard = ({ user, onLogout, onBackToDashboard }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [userActivity, setUserActivity] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [currentUser, setCurrentUser] = useState(user); // 👈 Local state for user

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // States for modals
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [showEntityJoinModal, setShowEntityJoinModal] = useState(false);
  const [showLicenseManagement, setShowLicenseManagement] = useState(false);

  // Handle body on component mount/unmount
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalBackground = document.body.style.background;
    const originalMargin = document.body.style.margin;
    const originalPadding = document.body.style.padding;

    document.body.style.overflow = 'auto';
    document.body.style.background = 'linear-gradient(to bottom right, #f8fafc, #eef2ff)';
    document.body.style.margin = '0';
    document.body.style.padding = '0';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.background = originalBackground;
      document.body.style.margin = originalMargin;
      document.body.style.padding = originalPadding;
    };
  }, []);

  useEffect(() => {
    // Check authentication on mount
    if (!isAuthenticated()) {
      logout();
      return;
    }
    loadUserStats();
    loadUserActivity();
    loadUserPreferences();
  }, []);

  const loadUserStats = async () => {
    try {
      const response = await apiClient.get('/api/user/stats');
      setStats(response.data);
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Erreur chargement stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserActivity = async () => {
    try {
      const response = await apiClient.get('/api/user/activity');
      setUserActivity(response.data);
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error(t('userDashboard.errors.loadActivity'), error);
    }
  };

  const loadUserPreferences = async () => {
    try {
      const response = await apiClient.get('/api/user/preferences');
      setPreferences(response.data);
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error(t('userDashboard.errors.loadPreferences'), error);
    }
  };

  // Function to handle user update
  const handleUserUpdated = (updatedUser) => {
    setCurrentUser(updatedUser);
    // 🔒 SÉCURITÉ: Mettre à jour le stockage sécurisé si nécessaire
    const currentAuth = secureStorage.getItem('authData') || {};
    if (currentAuth.user) {
      currentAuth.user = updatedUser;
      secureStorage.setItem('authData', currentAuth);
    }
  };

  // Actions focused on user profile
  const handleProfileAction = (action) => {
    switch (action) {
      case 'edit-profile':
        setShowEditProfileModal(true);
        break;
        
      case 'change-password':
        setShowChangePasswordModal(true);
        break;
        
      case 'preferences':
        setShowPreferencesModal(true);
        break;
        
      case 'join-entity':
        setShowEntityJoinModal(true);
        break;
        
      case 'export-data':
        handleExportUserData();
        break;
        
      case 'export-stats':
        handleExportStats();
        break;
        
      case 'export-graphs':
        handleExportGraphs();
        break;
        
      case 'export-results':
        handleExportResults();
        break;
        
      case 'delete-account':
        handleDeleteAccount();
        break;
        
      default:
        console.log('Action profil non implémentée:', action);
    }
  };

  const handleExportUserData = async () => {
    try {
      setLoading(true);
      
      const response = await apiClient.get('/api/user/export', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nodemented-user-data-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert(t('userDashboard.export.success'));
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Erreur export:', error);
      alert(t('userDashboard.export.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportStats = async () => {
    try {
      setLoading(true);
      // ✅ REMPLACER par apiClient
      const response = await apiClient.get('/api/user/export/stats', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nodemented-stats-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert(t('userDashboard.export.statsSuccess'));
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Erreur export stats:', error);
      alert(t('userDashboard.export.statsError'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportGraphs = async () => {
    try {
      setLoading(true);
      // ✅ REMPLACER par apiClient
      const response = await apiClient.get('/api/user/export/graphs', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nodemented-graphs-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('✅ Export de vos graphes terminé avec succès !');
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Erreur export graphes:', error);
      alert('❌ Erreur lors de l\'export des graphes');
    } finally {
      setLoading(false);
    }
  };

  const handleExportResults = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await apiClient.get('/api/user/export/results', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nodemented-results-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('✅ Export de vos résultats terminé avec succès !');
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Erreur export résultats:', error);
      alert('❌ Erreur lors de l\'export des résultats');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = () => {
    const confirmed = window.confirm(
      t('userDashboard.deleteAccount.warning')
    );
    
    if (confirmed) {
      const doubleConfirm = window.confirm(
        t('userDashboard.deleteAccount.finalConfirmation')
      );
      
      if (doubleConfirm) {
        const finalConfirm = prompt(t('userDashboard.deleteAccount.typeDeletePrompt'));
        if (finalConfirm === 'SUPPRIMER') {
          alert(t('userDashboard.deleteAccount.featureNotAvailable'));
        } else {
          alert(t('userDashboard.deleteAccount.cancelledIncorrectText'));
        }
      }
    }
  };

  const closeModals = () => {
    setShowEditProfileModal(false);
    setShowChangePasswordModal(false);
    setShowPreferencesModal(false);
  };

  return (
    <div className={styles.userDashboard}>
      {/* Header */}
      <div className={styles.dashboardHeader}>
        <div className={styles.headerLeft}>
          <button 
            onClick={onBackToDashboard}
            className={styles.backButton}
            title="Retour au tableau de bord principal"
          >
            ← Retour
          </button>
          <h1>👤 Mon Espace Personnel</h1>
          <p>Gestion de votre profil, statistiques et préférences</p>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.userInfo}>
            <span>{currentUser.email}</span>
            <span className={styles.userRole}>{currentUser.role}</span>
          </div>
          <button onClick={onLogout} className={styles.btnLogout}>
            🚪 Déconnexion
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className={styles.dashboardNav}>
        <button 
          className={`${styles.navButton} ${activeTab === 'overview' ? styles.navButtonActive : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Mes Statistiques
        </button>
        <button 
          className={`${styles.navButton} ${activeTab === 'activity' ? styles.navButtonActive : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          📈 Mon Activité
        </button>
        <button 
          className={`${styles.navButton} ${activeTab === 'profile' ? styles.navButtonActive : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          ⚙️ Mon Profil
        </button>
        <button 
          className={`${styles.navButton} ${activeTab === 'preferences' ? styles.navButtonActive : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          🎨 Préférences
        </button>
        <button 
          className={`${styles.navButton} ${activeTab === 'license' ? styles.navButtonActive : ''}`}
          onClick={() => setActiveTab('license')}
        >
          🎫 Ma Licence
        </button>
        <button 
          className={`${styles.navButton} ${activeTab === 'usage' ? styles.navButtonActive : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          📊 Usage Analytics
        </button>
        <button 
          className={`${styles.navButton} ${activeTab === 'entity-requests' ? styles.navButtonActive : ''}`}
          onClick={() => setActiveTab('entity-requests')}
        >
          🏢 Mes Demandes
        </button>
      </div>

      {/* Contenu */}
      <div className={styles.dashboardContent}>
        {activeTab === 'overview' && (
          <div>
            <h2 className={styles.overviewTitle}>📊 Mes Statistiques</h2>
            
            {/* Widget de statut de licence intégré */}
            <LicenseStatusWidget user={currentUser} />
            
            {loading ? (
              <div className={styles.loading}>Chargement de vos statistiques...</div>
            ) : (
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <h3 className={styles.statTitle}>Graphes créés</h3>
                  <div className={styles.statNumber}>{stats?.graphsCount || 0}</div>
                  <div className={styles.statDetail}>
                    {stats?.limits ? `Limite: ${stats.limits.maxNodes} nœuds (Plan ${stats.limits.plan})` : 'Total des graphes que vous avez créés'}
                  </div>
                </div>
                
                <div className={styles.statCard}>
                  <h3 className={styles.statTitle}>Nœuds analysés</h3>
                  <div className={styles.statNumber}>{stats?.nodesCount || 0}</div>
                  <div className={styles.statDetail}>
                    {stats?.breakdown && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                        {stats.breakdown.features > 0 && `${stats.breakdown.features} Features • `}
                        {stats.breakdown.apis > 0 && `${stats.breakdown.apis} APIs • `}
                        {stats.breakdown.services > 0 && `${stats.breakdown.services} Services`}
                      </div>
                    )}
                    Entités que vous avez analysées
                  </div>
                </div>
                
                <div className={styles.statCard}>
                  <h3 className={styles.statTitle}>Tests créés</h3>
                  <div className={styles.statNumber}>{stats?.testsCreated || 0}</div>
                  <div className={styles.statDetail}>Tests d'analyse que vous avez créés</div>
                </div>
                
                <div className={styles.statCard}>
                  <h3 className={styles.statTitle}>Tests validés</h3>
                  <div className={styles.statNumber}>{stats?.testsValidated || 0}</div>
                  <div className={styles.statDetail}>Tests passés avec succès</div>
                </div>
                
                <div className={styles.statCard}>
                  <h3 className={styles.statTitle}>Relations découvertes</h3>
                  <div className={styles.statNumber}>{stats?.relationsCount || 0}</div>
                  <div className={styles.statDetail}>Connexions trouvées dans vos analyses</div>
                </div>
                
                <div className={styles.statCard}>
                  <h3 className={styles.statTitle}>Temps d'utilisation</h3>
                  <div className={styles.statNumber}>{stats?.usageHours || 0}h</div>
                  <div className={styles.statDetail}>
                    {stats?.lastActivity && (
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        Dernière activité: {new Date(stats.lastActivity).toLocaleDateString()}
                      </div>
                    )}
                    Temps estimé passé sur la plateforme
                  </div>
                </div>
                
                <div className={styles.statCard}>
                  <h3 className={styles.statTitle}>Analyses effectuées</h3>
                  <div className={styles.statNumber}>{stats?.analysesCount || 0}</div>
                  <div className={styles.statDetail}>Nombre total d'analyses lancées</div>
                </div>
                
                <div className={styles.statCard}>
                  <h3 className={styles.statTitle}>Taux de réussite</h3>
                  <div className={styles.statNumber}>
                    {stats?.testsCreated && stats?.testsValidated 
                      ? Math.round((stats.testsValidated / stats.testsCreated) * 100) 
                      : 0}%
                  </div>
                  <div className={styles.statDetail}>
                    {stats?.testsCreated > 0 && (
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {stats.testsValidated}/{stats.testsCreated} tests réussis
                      </div>
                    )}
                    Pourcentage de tests réussis
                  </div>
                </div>
              </div>
            )}

            {/* Actions d'export uniquement */}
            <div className={styles.quickActions}>
              <h3 className={styles.quickActionsTitle}>
                📤 Export des données
              </h3>
              <div className={styles.actionsGrid}>
                <button className={styles.actionBtn} onClick={() => handleProfileAction('export-data')}>
                  <div className={styles.actionIcon}>
                    📤
                  </div>
                  <div className={styles.actionContent}>
                    <div className={styles.actionTitle}>Exporter mes données</div>
                    <div className={styles.actionDescription}>Télécharger une copie complète de toutes vos données (RGPD)</div>
                  </div>
                  <div className={styles.actionArrow}>→</div>
                </button>

                <button className={styles.actionBtn} onClick={() => handleProfileAction('export-stats')}>
                  <div className={styles.actionIcon}>
                    📊
                  </div>
                  <div className={styles.actionContent}>
                    <div className={styles.actionTitle}>Exporter mes statistiques</div>
                    <div className={styles.actionDescription}>Télécharger un rapport détaillé de votre activité</div>
                  </div>
                  <div className={styles.actionArrow}>→</div>
                </button>

                <button className={styles.actionBtn} onClick={() => handleProfileAction('export-graphs')}>
                  <div className={styles.actionIcon}>
                    🕸️
                  </div>
                  <div className={styles.actionContent}>
                    <div className={styles.actionTitle}>Exporter mes graphes</div>
                    <div className={styles.actionDescription}>Sauvegarder tous vos graphes au format JSON</div>
                  </div>
                  <div className={styles.actionArrow}>→</div>
                </button>

                <button className={styles.actionBtn} onClick={() => handleProfileAction('export-results')}>
                  <div className={styles.actionIcon}>
                    🔍
                  </div>
                  <div className={styles.actionContent}>
                    <div className={styles.actionTitle}>Exporter mes résultats</div>
                    <div className={styles.actionDescription}>Télécharger l'historique de vos analyses</div>
                  </div>
                  <div className={styles.actionArrow}>→</div>
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div>
            <h2>📈 Mon Activité Récente</h2>
            <div className={styles.activitySection}>
              <div className={styles.activityChart}>
                <h3>Activité des 30 derniers jours</h3>
                <p>Graphique d'activité à implémenter...</p>
              </div>
              
              <div className={styles.recentActions}>
                <h3>Actions récentes</h3>
                {userActivity.length > 0 ? (
                  <ul>
                    {userActivity.map((action, index) => (
                      <li key={index}>
                        <span className={styles.actionDate}>
                          {new Date(action.date).toLocaleDateString()}
                        </span>
                        <span className={styles.actionDescription}>{action.description}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Aucune activité récente trouvée.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div>
            <div className={styles.profileHeader}>
              <div className={styles.profileAvatar}>
                👤
              </div>
              <div className={styles.profileHeaderContent}>
                <h2 className={styles.profileHeaderTitle}>Mon Profil</h2>
                <p className={styles.profileHeaderSubtitle}>
                  Gérez vos informations personnelles et paramètres de compte
                </p>
              </div>
            </div>

            <div className={styles.profileSection}>
              <div className={styles.profileCard}>
                <h3 className={styles.profileCardTitle}>
                  📋 Informations personnelles
                </h3>
                <div className={styles.profileInfo}>
                  <div className={styles.profileField}>
                    <span className={styles.profileFieldLabel}>Email</span>
                    <span className={`${styles.profileFieldValue} ${styles.email}`}>
                      {currentUser.email}
                    </span>
                  </div>
                  <div className={styles.profileField}>
                    <span className={styles.profileFieldLabel}>Prénom</span>
                    <span className={styles.profileFieldValue}>
                      {currentUser.firstName || 'Non renseigné'}
                    </span>
                  </div>
                  <div className={styles.profileField}>
                    <span className={styles.profileFieldLabel}>Nom</span>
                    <span className={styles.profileFieldValue}>
                      {currentUser.lastName || 'Non renseigné'}
                    </span>
                  </div>
                  <div className={styles.profileField}>
                    <span className={styles.profileFieldLabel}>Rôle</span>
                    <span className={`${styles.profileFieldValue} ${styles.role}`}>
                      {currentUser.role}
                    </span>
                  </div>
                  <div className={styles.profileField}>
                    <span className={styles.profileFieldLabel}>Statut</span>
                    <span className={`${styles.profileFieldValue} ${styles.status}`}>
                      {currentUser.status}
                    </span>
                  </div>
                  <div className={styles.profileField}>
                    <span className={styles.profileFieldLabel}>Membre depuis</span>
                    <span className={styles.profileFieldValue}>
                      {currentUser.createdAt ? new Date(currentUser.createdAt).toLocaleDateString() : 'Non disponible'}
                    </span>
                  </div>
                </div>
              </div>

              {currentUser.organizationName && (
                <div className={styles.profileCard}>
                  <h3 className={styles.profileCardTitle}>
                    🏢 Organisation
                  </h3>
                  <div className={styles.profileInfo}>
                    <div className={styles.profileField}>
                      <span className={styles.profileFieldLabel}>Nom</span>
                      <span className={styles.profileFieldValue}>
                        {currentUser.organizationName}
                      </span>
                    </div>
                    <div className={styles.profileField}>
                      <span className={styles.profileFieldLabel}>Tenant ID</span>
                      <span className={`${styles.profileFieldValue} ${styles.tenantId}`}>
                        {currentUser.tenantId}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions de gestion du profil */}
              <div className={styles.profileActions}>
                <h3 className={styles.profileActionsTitle}>
                  🛠️ Gestion du profil
                </h3>
                <div className={styles.profileActionsGrid}>
                  <button className={styles.profileActionBtn} onClick={() => handleProfileAction('edit-profile')}>
                    <div className={styles.profileActionIcon}>
                      ✏️
                    </div>
                    <div className={styles.profileActionContent}>
                      <div className={styles.profileActionTitle}>{t('userDashboard.actions.editProfile.title')}</div>
                      <div className={styles.profileActionDescription}>
                        {t('userDashboard.actions.editProfile.description')}
                      </div>
                    </div>
                  </button>

                  <button className={styles.profileActionBtn} onClick={() => handleProfileAction('preferences')}>
                    <div className={styles.profileActionIcon}>
                      🎨
                    </div>
                    <div className={styles.profileActionContent}>
                      <div className={styles.profileActionTitle}>{t('userDashboard.actions.preferences.title')}</div>
                      <div className={styles.profileActionDescription}>
                        {t('userDashboard.actions.preferences.description')}
                      </div>
                    </div>
                  </button>

                  <button className={styles.profileActionBtn} onClick={() => handleProfileAction('change-password')}>
                    <div className={styles.profileActionIcon}>
                      🔐
                    </div>
                    <div className={styles.profileActionContent}>
                      <div className={styles.profileActionTitle}>{t('userDashboard.actions.changePassword.title')}</div>
                      <div className={styles.profileActionDescription}>
                        {t('userDashboard.actions.changePassword.description')}
                      </div>
                    </div>
                  </button>

                  <button className={styles.profileActionBtn} onClick={() => handleProfileAction('join-entity')}>
                    <div className={styles.profileActionIcon}>
                      🏢
                    </div>
                    <div className={styles.profileActionContent}>
                      <div className={styles.profileActionTitle}>{t('userDashboard.actions.joinOrganization.title')}</div>
                      <div className={styles.profileActionDescription}>
                        {t('userDashboard.actions.joinOrganization.description')}
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Section séparée pour les actions dangereuses */}
              <div className={styles.profileActions}>
                <h3 className={styles.profileActionsTitle}>
                  ⚠️ {t('userDashboard.dangerZone.title')}
                </h3>
                <div className={styles.profileActionsGrid}>
                  <button 
                    className={`${styles.profileActionBtn} ${styles.dangerAction}`} 
                    onClick={() => handleProfileAction('delete-account')}
                  >
                    <div className={styles.profileActionIcon}>
                      🗑️
                    </div>
                    <div className={styles.profileActionContent}>
                      <div className={styles.profileActionTitle}>{t('userDashboard.actions.deleteAccount.title')}</div>
                      <div className={styles.profileActionDescription}>
                        {t('userDashboard.actions.deleteAccount.description')}
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div>
            <h2>🎨 {t('userDashboard.preferences.title')}</h2>
            <div className={styles.preferencesSection}>
              <div className={styles.preferenceCard}>
                <h3>🌙 {t('userDashboard.preferences.theme.title')}</h3>
                <p>{t('userDashboard.preferences.theme.description')}</p>
                <select value={preferences.theme || 'light'}>
                  <option value="light">{t('userDashboard.preferences.theme.light')}</option>
                  <option value="dark">{t('userDashboard.preferences.theme.dark')}</option>
                  <option value="auto">{t('userDashboard.preferences.theme.auto')}</option>
                </select>
              </div>
              
              <div className={styles.preferenceCard}>
                <h3>🔔 {t('userDashboard.preferences.notifications.title')}</h3>
                <p>{t('userDashboard.preferences.notifications.description')}</p>
                <label>
                  <input type="checkbox" checked={preferences.emailNotifications || false} />
                  {t('userDashboard.preferences.notifications.email')}
                </label>
              </div>
              
              <div className={styles.preferenceCard}>
                <h3>🌍 {t('userDashboard.preferences.language.title')}</h3>
                <p>{t('userDashboard.preferences.language.description')}</p>
                <select value={preferences.language || 'fr'}>
                  <option value="fr">{t('userDashboard.preferences.language.french')}</option>
                  <option value="en">{t('userDashboard.preferences.language.english')}</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'license' && (
          <LicenseManagement 
            user={currentUser}
            onLicenseUpdate={() => {
              // Callback to refresh data after license change
              console.log('Licence mise à jour');
            }}
          />
        )}

        {activeTab === 'usage' && (
          <div>
            <h2 style={{ marginBottom: '1.5rem', color: '#1e40af' }}>📊 {t('userDashboard.usage.title')}</h2>
            <UsageDashboard user={currentUser} />
          </div>
        )}

        {activeTab === 'entity-requests' && (
          <div>
            <h2>🏢 {t('userDashboard.entityManagement.title')}</h2>
            
            {/* Component to create a new request */}
            {(!currentUser.organizationId || currentUser.role === 'user') && (
              <div style={{ marginBottom: '30px' }}>
                <EntityJoinRequest 
                  user={currentUser}
                  onSuccess={() => {
                    // Optional: refresh data after success
                    window.location.reload();
                  }}
                />
              </div>
            )}

            {/* Afficher les demandes existantes */}
            <UserEntityRequests user={currentUser} />
          </div>
        )}
      </div>

      {/* Modal d'édition du profil - UTILISATION DU VRAI COMPOSANT */}
      <EditProfileModal
        user={currentUser}
        isOpen={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
        onUserUpdated={handleUserUpdated}
      />

      {/* Modal de demande d'adhésion à une entité */}
      {showEntityJoinModal && (
        <EntityJoinModal
          isOpen={showEntityJoinModal}
          onClose={() => setShowEntityJoinModal(false)}
          user={currentUser}
        />
      )}

      {/* Autres modales simplifiées */}
      {showChangePasswordModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowChangePasswordModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: 'bold' }}>
              🔐 {t('userDashboard.changePasswordModal.title')}
            </h3>
            <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280' }}>
              {t('userDashboard.changePasswordModal.description')}
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button 
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setShowEditProfileModal(true);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Modifier mon profil
              </button>
              <button 
                onClick={() => setShowChangePasswordModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreferencesModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowPreferencesModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: 'bold' }}>
              🎨 {t('userDashboard.preferences.title')}
            </h3>
            <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280' }}>
              {t('userDashboard.preferences.comingSoon')}
            </p>
            <button 
              onClick={() => setShowPreferencesModal(false)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;